import { type TutorialStep, STEPS } from "../constants/tutorial-steps";
import { TutorialHighlight } from "./TutorialHighlight";

interface TutorialGuidedStepProps {
  readonly current: TutorialStep;
  readonly step: number;
  readonly validated: boolean;
  readonly onPrev: () => void;
  readonly onNext: () => void;
  readonly onSkip: () => void;
  readonly isLast: boolean;
}

function StepDots() {
  return (
    <div className="flex items-center gap-1.5">
      {STEPS.map((_, i) => (
        <div
          key={i}
          className={`h-1.5 rounded-full transition-all ${
            i === STEPS.findIndex((s) => s === STEPS[i])
              ? "bg-accent w-4"
              : i < STEPS.findIndex((s) => s === STEPS[i])
                ? "bg-accent/40 w-1.5"
                : "bg-ink-faint/30 w-1.5"
          }`}
        />
      ))}
    </div>
  );
}

function StepActions({
  step,
  isLast,
  onPrev,
  onNext,
  onSkip,
}: {
  step: number;
  isLast: boolean;
  onPrev: () => void;
  onNext: () => void;
  onSkip: () => void;
}) {
  return (
    <div className="flex items-center justify-between w-full gap-3">
      <button
        type="button"
        onClick={onSkip}
        className="text-[11px] text-ink-muted hover:text-ink transition-colors"
      >
        Passer
      </button>
      <div className="flex items-center gap-2">
        {step > 0 && (
          <button
            type="button"
            onClick={onPrev}
            className="px-3 py-2 text-xs font-medium text-ink-muted hover:text-ink rounded-md hover:bg-surface-alt transition-colors"
          >
            Précédent
          </button>
        )}
        <button
          type="button"
          onClick={onNext}
          className="px-3 py-2 text-xs font-medium text-white bg-accent rounded-md hover:opacity-90 transition-opacity"
        >
          {isLast ? "Terminer" : "Suivant"}
        </button>
      </div>
    </div>
  );
}

export function TutorialGuidedStep({
  current,
  step,
  validated,
  onPrev,
  onNext,
  onSkip,
  isLast,
}: TutorialGuidedStepProps) {
  const stepLabel = `${step + 1} / ${STEPS.length}`;

  return (
    <>
      {current.target && !validated && (
        <TutorialHighlight target={current.target} />
      )}
      <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[70] w-full max-w-md px-4 animate-slide-up">
        <div className="bg-surface rounded-2xl shadow-xl border border-border p-5 flex flex-col gap-3">
          {validated ? (
            <div className="flex items-center gap-3 py-2">
              <span className="text-2xl">{"\u2705"}</span>
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                  Bravo !
                </span>
                <span className="text-xs text-ink-muted">
                  Passage à l'étape suivante...
                </span>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3">
              <span className="text-2xl shrink-0">{current.icon}</span>
              <div className="flex flex-col gap-1.5 min-w-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-ink">
                    {current.title}
                  </h3>
                  <span className="text-[10px] text-ink-faint font-medium">
                    {stepLabel}
                  </span>
                </div>
                <p className="text-xs text-ink-muted leading-relaxed">
                  {current.instruction}
                </p>
                {current.action && (
                  <p className="text-[11px] text-accent font-medium mt-0.5">
                    ▶ {current.action}
                  </p>
                )}
              </div>
            </div>
          )}
          {!validated && (
            <>
              <StepDots />
              <StepActions
                step={step}
                isLast={isLast}
                onPrev={onPrev}
                onNext={onNext}
                onSkip={onSkip}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
}
